import React, { useCallback, useState } from 'react';
import { useAuth } from '../../../../contexts/AuthContext';
import { stcBinService } from '../../../../services/stcBinService';
import { itemBinSizeService } from '../../../../services/itemBinSizeService';
import { consolidationService } from '../../../../services/consolidationService';
import { CapacityUpdateModal } from '../../../shared/CapacityUpdateModal';

/** Argumenter for å åpne kapasitets-modalen for en (lokasjon, vare). */
export interface OpenCapacityArgs {
    binCode: string;
    itemNo: string;
    itemDescription?: string;
    /** Saldo i mål-binnen for varen (vises i modalen; valgfritt). */
    currentQuantity?: number;
}

interface UseCapacityUpdateOptions {
    warehouseID: string;
    /** Kalles etter vellykket lagring — typisk for å re-analysere forslagene. */
    onSaved?: () => void;
    /** Kalles ved feil (oppslag/lagring) — typisk for å vise melding. */
    onError?: (message: string) => void;
}

interface ModalData {
    itemNo: string;
    itemDescription?: string;
    binCode: string;
    binSizeNo: number;
    currentQuantity?: number;
    currentMaxQuantity?: number;
    existingItemBinSizeId: number | null;
    existingPutAwayPriorityNo: number;
}

/**
 * Gjenbrukbar «Oppdater kapasitet»-flyt for konsolideringen. Slår opp mål-binnens
 * BinSizeNo + eksisterende ItemBinSize for varen, og åpner den delte CapacityUpdateModal.
 * Lagring rutes gjennom consolidationService.registerCapacityFeedback slik at provenansen
 * settes til CAPACITY-FEEDBACK (graderer kapasiteten til «observert») og et domenehendelse
 * logges for audit/KPI.
 *
 * Bruk: `const cap = useCapacityUpdate({ warehouseID, onSaved }); ... cap.open({...}); {cap.modal}`
 */
export const useCapacityUpdate = ({ warehouseID, onSaved, onError }: UseCapacityUpdateOptions) => {
    const { user } = useAuth();
    const companyNo = user?.companyNo || '';

    const [data, setData] = useState<ModalData | null>(null);
    const [isLoading, setIsLoading] = useState(false);

    const open = useCallback(async (args: OpenCapacityArgs) => {
        if (!args.binCode?.trim() || !args.itemNo?.trim()) {
            onError?.('Mangler lokasjon eller vare for kapasitetsoppdatering.');
            return;
        }

        setIsLoading(true);
        try {
            const bin = await stcBinService.getByExactBinCode(companyNo, args.binCode.trim());
            if (!bin) {
                onError?.(`Lokasjonen ${args.binCode} ble ikke funnet.`);
                return;
            }

            const existing = await itemBinSizeService
                .getByCompanyItemAndBinSize(companyNo, args.itemNo, bin.binSizeNo)
                .catch(() => null);

            setData({
                itemNo: args.itemNo,
                itemDescription: args.itemDescription,
                binCode: args.binCode.trim(),
                binSizeNo: bin.binSizeNo,
                currentQuantity: args.currentQuantity,
                currentMaxQuantity: existing?.maxQuantity,
                existingItemBinSizeId: existing?.id ?? null,
                existingPutAwayPriorityNo: existing?.putAwayPriorityNo ?? bin.putAwayPriorityNo ?? 1,
            });
        } catch (err) {
            onError?.(err instanceof Error ? err.message : 'Kunne ikke laste kapasitetsdata.');
        } finally {
            setIsLoading(false);
        }
    }, [companyNo, onError]);

    const close = useCallback(() => setData(null), []);

    const modal = data ? (
        <CapacityUpdateModal
            isOpen
            onClose={close}
            itemNo={data.itemNo}
            itemDescription={data.itemDescription}
            binCode={data.binCode}
            binSizeNo={data.binSizeNo}
            currentQuantity={data.currentQuantity}
            currentMaxQuantity={data.currentMaxQuantity}
            existingItemBinSizeId={data.existingItemBinSizeId}
            existingPutAwayPriorityNo={data.existingPutAwayPriorityNo}
            saveOverride={async (newMax) => {
                const result = await consolidationService.registerCapacityFeedback({
                    warehouseID,
                    binCode: data.binCode,
                    itemNo: data.itemNo,
                    observedMaxQuantity: newMax,
                });
                if (!result.success) {
                    throw new Error(result.errorMessage || 'Kunne ikke lagre kapasitet.');
                }
            }}
            onSaved={async () => {
                setData(null);
                onSaved?.();
            }}
        />
    ) : null;

    return { open, close, modal, isLoading };
};
